<template>
  <transition name="wnd-fade">
    <div
      v-if="!isGuest && open"
      class="wnd-overlay theme-turquoise"
      @click.self="dismiss"
    >
      <div
        class="wnd-card"
        role="dialog"
        aria-modal="true"
        aria-label="What's new in Alice"
      >
        <button
          class="wnd-close"
          type="button"
          aria-label="Close"
          @click="dismiss"
        >
          ×
        </button>

        <div class="wnd-body">
          <div class="wnd-eyebrow">Changelog</div>
          <h2 class="wnd-title">What's new in Alice</h2>

          <ul class="wnd-list">
            <li v-for="e in entries" :key="e.id" class="wnd-entry">
              <div class="wnd-entry__date">{{ shortDate(e.date) }}</div>
              <div class="wnd-entry__content">
                <div class="wnd-entry__head">
                  <span class="wnd-tag" :class="`wnd-tag--${e.type}`">{{
                    typeLabel(e.type)
                  }}</span>
                  <h3 class="wnd-entry__title">{{ e.title }}</h3>
                </div>
                <p v-if="e.body" class="wnd-entry__body">{{ e.body }}</p>
              </div>
            </li>
          </ul>
          <p v-if="moreCount > 0" class="wnd-more">
            …and {{ moreCount }} more since your last visit.
          </p>
        </div>

        <div class="wnd-foot">
          <button
            class="wnd-btn wnd-btn--ghost"
            type="button"
            @click="viewChangelog"
          >
            View changelog
          </button>
          <button class="wnd-btn" type="button" @click="dismiss">Got it</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
const TYPE_LABELS = { new: "New", improved: "Improved", fixed: "Fixed" };

export default {
  name: "WhatsNewDialog",
  computed: {
    isGuest() {
      return this.$store.getters.isGuest;
    },
    open() {
      return this.$store.state.whatsNewOpen;
    },
    entries() {
      return this.$store.state.whatsNewEntries;
    },
    moreCount() {
      return Math.max(0, this.$store.state.whatsNewTotal - this.entries.length);
    },
  },
  methods: {
    typeLabel(type) {
      return TYPE_LABELS[type] || type;
    },
    shortDate(date) {
      if (!date) return "";
      const d = new Date(date + "T00:00:00");
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    },
    dismiss() {
      this.$store.dispatch("markWhatsNewSeen");
    },
    viewChangelog() {
      this.dismiss();
      this.$router.push("/changelog");
    },
  },
};
</script>

<style scoped>
.wnd-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(20, 25, 25, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.wnd-card {
  position: relative;
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
  font-family: var(--font-body);
}
.wnd-close {
  position: absolute;
  top: 8px;
  right: 10px;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: var(--fg-3);
  cursor: pointer;
}
.wnd-close:hover {
  color: var(--vp-black);
}

.wnd-body {
  padding: 22px 26px 4px;
}
.wnd-eyebrow {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.wnd-title {
  margin: 0 0 6px;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--vp-black);
}

.wnd-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.wnd-entry {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 12px 0;
  border-bottom: 1px solid var(--border-hairline);
}
.wnd-entry:last-child {
  border-bottom: none;
}
.wnd-entry__date {
  width: 52px;
  flex-shrink: 0;
  padding-top: 2px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  color: var(--fg-3);
}
.wnd-entry__content {
  flex: 1;
  min-width: 0;
}
.wnd-entry__head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.wnd-entry__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 700;
  color: var(--vp-black);
}
.wnd-entry__body {
  margin: 4px 0 0;
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.5;
  color: var(--fg-2);
}

.wnd-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1;
}
.wnd-tag--new {
  color: var(--vp-turquoise-160);
  background: var(--vp-turquoise-20);
}
.wnd-tag--improved {
  color: var(--vp-geel-160);
  background: var(--vp-geel-20);
}
.wnd-tag--fixed {
  color: var(--vp-grijs-160);
  background: var(--vp-grijs-20);
}

.wnd-more {
  margin: 10px 0 0;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}

.wnd-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 20px 26px 24px;
  gap: 8px;
}
.wnd-btn {
  height: 38px;
  padding: 0 16px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: 1px solid var(--vp-black);
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  transition: background var(--dur-fast) var(--ease-hover),
    color var(--dur-fast) var(--ease-hover);
}
.wnd-btn:hover {
  background: var(--accent-100);
  border-color: var(--accent-100);
  color: var(--vp-black);
}
.wnd-btn--ghost {
  background: var(--vp-white);
  color: var(--fg-2);
  border-color: var(--border-hairline);
}
.wnd-btn--ghost:hover {
  background: var(--vp-white);
  border-color: var(--vp-black);
  color: var(--vp-black);
}

.wnd-fade-enter-active,
.wnd-fade-leave-active {
  transition: opacity var(--dur-base) var(--ease-out);
}
.wnd-fade-enter-from,
.wnd-fade-leave-to {
  opacity: 0;
}
</style>
